/**
 * A porta entre a tela da telemetria (módulo 17; 0103) e o servidor. A mesa e o
 * celular usam as mesmas chamadas e os mesmos ganchos de leitura.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { comCorpo, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import type {
  DetalheDoDispositivo,
  FiltroDaFila,
  Formato,
  RespostaDaChave,
  RespostaDaConciliacao,
  RespostaDaFila,
  RespostaDasIntegracoes,
  RespostaDoSinal,
  TipoDeMensagem,
  UltimoValor,
} from './telemetria';
import { retornoDoReprocesso } from './telemetria';

export interface TelemetriaDaMaquina {
  readonly dispositivos: readonly {
    readonly dispositivoId: string;
    readonly identificador: string;
    readonly integracao: string;
    readonly desde: string;
    readonly conferidoEm: string | null;
    readonly ultimoSinalEm: string | null;
  }[];
  readonly ultimos: readonly UltimoValor[];
  readonly dias: readonly { readonly dia: string; readonly horasMotor: number | null; readonly horasOciosas: number | null }[];
}

export const apiDaTelemetria = {
  sinal: () => pedir<RespostaDoSinal>('/telemetria/sinal'),
  dispositivo: (id: string) => pedir<DetalheDoDispositivo>(`/telemetria/dispositivos/${encodeURIComponent(id)}`),
  fila: (situacao: FiltroDaFila, pagina: number) => pedir<RespostaDaFila>(`/telemetria/fila?situacao=${situacao}&pagina=${String(pagina)}`),
  conciliacao: (de: string | undefined, ate: string | undefined, pagina: number) =>
    pedir<RespostaDaConciliacao>(
      `/telemetria/conciliacao?pagina=${String(pagina)}${de ? `&de=${de}` : ''}${ate ? `&ate=${ate}` : ''}`,
    ),
  integracoes: () => pedir<RespostaDasIntegracoes>('/telemetria/integracoes'),
  maquina: (id: string) => pedir<TelemetriaDaMaquina>(`/telemetria/maquinas/${encodeURIComponent(id)}`),
  maquinasParaLigar: (termo: string) =>
    pedir<{ maquinas: readonly { id: string; codigo: string; descricao: string }[] }>(
      `/telemetria/maquinas-para-ligar?termo=${encodeURIComponent(termo)}`,
    ),

  descartar: (ids: readonly string[], motivo: string) =>
    comCorpo<{ descartadas: number }>('POST', '/telemetria/fila/descarte', { ids, motivo }),
  reprocessar: (ids: readonly string[]) =>
    comCorpo<{ reprocessadas: number; aceitas: number; continuamNaFila: number }>('POST', '/telemetria/fila/reprocesso', { ids }),
  cadastrarIntegracao: (d: { nome: string; formato: Formato; campos: readonly TipoDeMensagem[]; frequenciaMin: number; licencaAte: string | null }) =>
    comCorpo<{ id: string; nome: string; campos: number }>('POST', '/telemetria/integracoes', d),
  editarIntegracao: (id: string, d: { campos: readonly TipoDeMensagem[]; frequenciaMin: number; licencaAte: string | null; ativa: boolean }) =>
    comCorpo<{ nome: string; campos: number; ativa: boolean }>('POST', `/telemetria/integracoes/${encodeURIComponent(id)}/edicao`, d),
  emitirChave: (integracaoId: string) =>
    comCorpo<RespostaDaChave>('POST', `/telemetria/integracoes/${encodeURIComponent(integracaoId)}/chaves`, {}),
  revogarChave: (id: string, motivo: string) =>
    comCorpo<{ prefixo: string; revogada: boolean }>('POST', `/telemetria/chaves/${encodeURIComponent(id)}/revogacao`, { motivo }),
  cadastrarDispositivo: (d: { integracaoId: string; identificador: string; descricao: string | null }) =>
    comCorpo<{ id: string; identificador: string; integracao: string }>('POST', '/telemetria/dispositivos', d),
  ligar: (d: { dispositivoId: string; equipamentoId: string; inicio: string }) =>
    comCorpo<{ id: string; identificador: string; equipamento: string; inicio: string }>('POST', '/telemetria/vinculos', d),
  encerrar: (vinculoId: string, fim: string) =>
    comCorpo<{ identificador: string; equipamento: string; fim: string }>('POST', `/telemetria/vinculos/${encodeURIComponent(vinculoId)}/encerramento`, { fim }),
  conferir: (vinculoId: string, leituraDoPainel: number, leituraDoDispositivo: number) =>
    comCorpo<{ equipamento: string; diferenca: number }>('POST', `/telemetria/vinculos/${encodeURIComponent(vinculoId)}/conferencia`, {
      leituraDoPainel,
      leituraDoDispositivo,
    }),
  regras: (d: { semSinalVezes: number; ociosidadePct: number; toleranciaConsumoPct: number; motivo: string }) =>
    comCorpo<{ semSinalVezes: number; ociosidadePct: number; toleranciaConsumoPct: number }>('POST', '/telemetria/regras', d),
};

/** As leituras, com a chave do cache que as gravações invalidam (tudo sob `telemetria`). */
export const useSinal = (ligado = true) =>
  useQuery({ queryKey: ['telemetria', 'sinal'], queryFn: apiDaTelemetria.sinal, enabled: ligado });
export const useDispositivo = (id: string | null) =>
  useQuery({ queryKey: ['telemetria', 'dispositivo', id], queryFn: () => apiDaTelemetria.dispositivo(id ?? ''), enabled: id !== null });
export const useFila = (situacao: FiltroDaFila, pagina: number) =>
  useQuery({ queryKey: ['telemetria', 'fila', situacao, pagina], queryFn: () => apiDaTelemetria.fila(situacao, pagina) });
export const useConciliacaoDaTelemetria = (de: string | undefined, ate: string | undefined, pagina: number) =>
  useQuery({ queryKey: ['telemetria', 'conciliacao', de, ate, pagina], queryFn: () => apiDaTelemetria.conciliacao(de, ate, pagina) });
export const useIntegracoes = () => useQuery({ queryKey: ['telemetria', 'integracoes'], queryFn: apiDaTelemetria.integracoes });
export const useTelemetriaDaMaquina = (id: string, ligado: boolean) =>
  useQuery({ queryKey: ['telemetria', 'maquina', id], queryFn: () => apiDaTelemetria.maquina(id), enabled: ligado });
export const useMaquinasParaLigar = (termo: string, ligado: boolean) =>
  useQuery({ queryKey: ['telemetria', 'maquinas-para-ligar', termo], queryFn: () => apiDaTelemetria.maquinasParaLigar(termo), enabled: ligado });

/** O reprocesso não pede nada: só confirma, e o retorno diz quantas entraram. */
export function useReprocessar() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ids: readonly string[]) => apiDaTelemetria.reprocessar(ids),
    meta: {
      retorno: retornoDe<{ reprocessadas: number; aceitas: number; continuamNaFila: number }, readonly string[]>((r) => ({ titulo: r.reprocessadas === 1 ? 'Mensagem reprocessada' : 'Mensagens reprocessadas', ...retornoDoReprocesso(r) })),
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['telemetria'] });
    },
  });
}
